/* ── WORK_TRACK — Mentions (@mentions) ───────────────────────
   Highlighting, autocomplete while typing, and the in-app
   notification inbox (profile menu). Shared by task comments,
   chat, and meeting chat.
   ─────────────────────────────────────────────────────────── */

import { $, $$, esc, state, api, avatarHtml, fmtDate, toast } from './core';
import type { Member, Mention } from './types';

const MENTION_TOKEN_RE = /(?<![a-zA-Z0-9_@.+-])@([a-zA-Z0-9_.-]{1,32})/g;
const URL_RE = /https?:\/\/[^\s<>"']+/g;

function linkify(html: string): string {
  return html.replace(URL_RE, match => {
    const attrs = (() => {
      try {
        return new URL(match).origin === location.origin ? '' : ' target="_blank" rel="noopener noreferrer"';
      } catch {
        return ' target="_blank" rel="noopener noreferrer"';
      }
    })();
    return `<a href="${match}"${attrs}>${match}</a>`;
  });
}

export function highlightMentions(escaped: string): string {
  return linkify(escaped).replace(MENTION_TOKEN_RE, '<span class="mention">@$1</span>');
}

// ── Autocomplete ─────────────────────────────────────────
interface Picker {
  input: HTMLInputElement | HTMLTextAreaElement;
}

let picker: Picker | null = null;
let pickerMembers: Member[] = [];
let pickerIndex = 0;
let menuEl: HTMLDivElement | null = null;

function getMenu(): HTMLDivElement {
  if (!menuEl) {
    menuEl = document.createElement('div');
    menuEl.className = 'mention-menu hidden';
    document.body.appendChild(menuEl);
    menuEl.addEventListener('mousedown', e => e.preventDefault());
    menuEl.addEventListener('click', e => {
      const row = (e.target as HTMLElement).closest<HTMLElement>('[data-username]');
      if (row && row.dataset.username) applyMention(row.dataset.username);
    });
  }
  return menuEl;
}

function activeToken(input: HTMLInputElement | HTMLTextAreaElement): { start: number; q: string } | null {
  const v = input.value;
  const pos = input.selectionStart ?? v.length;
  const before = v.slice(0, pos);
  const m = before.match(/@([a-zA-Z0-9_.-]*)$/);
  if (!m) return null;
  const atIdx = pos - m[0].length;
  const prev = atIdx > 0 ? before[atIdx - 1] : '';
  if (prev && /[a-zA-Z0-9@]/.test(prev)) return null;
  return { start: atIdx, q: m[1].toLowerCase() };
}

function hideMenu(): void {
  if (menuEl) menuEl.classList.add('hidden');
  picker = null;
  pickerMembers = [];
}

// True when the mention autocomplete menu is visible (its Enter key owns the keystroke).
export function mentionPickerActive(): boolean {
  return !!menuEl && !menuEl.classList.contains('hidden') && !!picker;
}

function scrollActiveIntoView(): void {
  const menu = getMenu();
  const active = menu.querySelector<HTMLElement>('.mention-row.active');
  if (!active) return;
  if (active.offsetTop < menu.scrollTop) menu.scrollTop = active.offsetTop;
  else if (active.offsetTop + active.offsetHeight > menu.scrollTop + menu.clientHeight) {
    menu.scrollTop = active.offsetTop + active.offsetHeight - menu.clientHeight;
  }
}

function renderActive(): void {
  const menu = getMenu();
  $$<HTMLElement>('.mention-row', menu).forEach((el, i) => el.classList.toggle('active', i === pickerIndex));
  scrollActiveIntoView();
}

function updateMenu(input: HTMLInputElement | HTMLTextAreaElement, getMembers: () => Member[]): void {
  const tok = activeToken(input);
  if (!tok) {
    hideMenu();
    return;
  }
  const me = state.user ? state.user.id : null;
  const list = getMembers()
    .filter(m => m.status !== 'disabled' && m.id !== me)
    .filter(m => ((m.username || '') + ' ' + (m.name || '')).toLowerCase().includes(tok.q))
    .slice(0, 8);
  picker = { input };
  pickerMembers = list;
  pickerIndex = 0;
  if (!list.length) {
    hideMenu();
    return;
  }
  const menu = getMenu();
  menu.innerHTML = list
    .map(
      (
        m,
        i
      ) => `<button type="button" class="mention-row ${i === 0 ? 'active' : ''}" data-username="${esc(m.username)}">
        ${avatarHtml((m.name || m.username).slice(0, 2).toUpperCase(), m.id, m.avatar || null)}
        <span class="mention-row-name">${esc(m.name || m.username)}</span>
        <span class="mention-row-user">@${esc(m.username)}</span>
      </button>`
    )
    .join('');
  const rect = input.getBoundingClientRect();
  menu.style.top = `${rect.bottom + 4}px`;
  menu.style.left = `${rect.left}px`;
  menu.style.minWidth = `${Math.max(200, rect.width)}px`;
  menu.classList.remove('hidden');
  scrollActiveIntoView();
}

function applyMention(username: string): void {
  const p = picker;
  if (!p) return;
  const input = p.input;
  const tok = activeToken(input);
  if (!tok) {
    hideMenu();
    return;
  }
  const v = input.value;
  const caret = input.selectionStart ?? v.length;
  const prefix = v.slice(0, tok.start);
  const suffix = v.slice(caret);
  input.value = `${prefix}@${username} ${suffix}`;
  const newCaret = (prefix + '@' + username + ' ').length;
  input.setSelectionRange(newCaret, newCaret);
  hideMenu();
  input.focus();
}

function movePicker(dir: number): void {
  if (!pickerMembers.length) return;
  pickerIndex = (pickerIndex + dir + pickerMembers.length) % pickerMembers.length;
  renderActive();
}

function onKeydown(e: Event): void {
  if (!menuEl || menuEl.classList.contains('hidden')) return;
  const key = (e as KeyboardEvent).key;
  if (key === 'ArrowDown') {
    e.preventDefault();
    movePicker(1);
  } else if (key === 'ArrowUp') {
    e.preventDefault();
    movePicker(-1);
  } else if ((key === 'Enter' || key === 'Tab') && pickerMembers.length) {
    e.preventDefault();
    e.stopPropagation();
    applyMention(pickerMembers[pickerIndex].username);
  } else if (key === 'Escape') {
    e.preventDefault();
    e.stopPropagation();
    hideMenu();
  }
}

export function attachMentionAutocomplete(
  input: HTMLInputElement | HTMLTextAreaElement,
  getMembers: () => Member[]
): void {
  input.addEventListener('input', () => updateMenu(input, getMembers));
  input.addEventListener('keydown', onKeydown);
  input.addEventListener('blur', () => setTimeout(hideMenu, 150));
}

// ── Inbox (bell) ─────────────────────────────────────────
let commentOpen: ((taskId: number) => void) | null = null;
let chatOpen: ((channel: string) => void) | null = null;

export function setCommentMentionHandler(fn: (taskId: number) => void): void {
  commentOpen = fn;
}

export function setChatMentionHandler(fn: (channel: string) => void): void {
  chatOpen = fn;
}

function chatChannelLabel(ch: string | null): string {
  if (!ch) return 'a chat';
  if (ch === 'general') return '#general';
  if (ch.startsWith('dm:')) {
    const me = state.user ? state.user.id : null;
    const peerId = (ch.slice(3).split('-').map(Number) || []).find(id => id !== me) || 0;
    const peer = state.members.find(m => m.id === peerId);
    return peer ? `DM with ${esc(peer.name || peer.username)}` : 'a DM';
  }
  if (ch.startsWith('meeting:')) return 'a meeting';
  if (ch.startsWith('project:')) return 'a project chat';
  if (ch.startsWith('ticket:')) return 'a ticket chat';
  return esc(ch);
}

function renderMentionItem(m: Mention): string {
  const author = esc(m.author_name || m.author_username || 'Someone');
  const ctx =
    m.source_type === 'comment'
      ? `mentioned you in a comment on ${m.task_title ? `“${esc(m.task_title)}”` : 'a task'}`
      : m.source_type === 'call'
        ? 'declined your call'
        : m.source_type === 'missed_call'
          ? 'tried to call you'
          : `mentioned you in ${chatChannelLabel(m.channel)}`;
  return `<li class="mention-item ${m.read ? '' : 'unread'}" data-stype="${m.source_type}" data-task="${m.task_id || ''}" data-channel="${esc(m.channel || '')}">
      <div class="mention-item-main"><strong>${author}</strong> ${ctx}
        <span class="mention-item-time">${fmtDate(m.created_at)}</span>
      </div>
      ${m.snippet ? `<div class="mention-item-snippet">${highlightMentions(esc(m.snippet))}</div>` : ''}
    </li>`;
}

let unreadCount = 0;

export async function refreshUnread(): Promise<void> {
  if (!state.token) {
    unreadCount = 0;
    applyNotifIcon();
    return;
  }
  try {
    const d = await api<{ count: number }>('/api/mentions/unread');
    unreadCount = Number(d.count) || 0;
    applyNotifIcon();
  } catch {
    /* offline / older backend — ignore */
  }
}

// ── Notification setting ───────────────────────────────
const NOTIF_KEY = 'wt_notif';

export function notifEnabled(): boolean {
  try {
    return localStorage.getItem(NOTIF_KEY) === '1';
  } catch {
    return false;
  }
}

export function setNotifEnabled(on: boolean): void {
  try {
    localStorage.setItem(NOTIF_KEY, on ? '1' : '0');
  } catch {
    /* ignore */
  }
}

export function applyNotifIcon(): void {
  const item = $('#profileNotifsBtn');
  const badge = $('#profileNotifsBadge');
  const dot = $('#profileNotifDot');
  const on = notifEnabled();
  if (item) item.classList.toggle('hidden', !on);
  if (!on) {
    if (badge) badge.classList.add('hidden');
    if (dot) dot.classList.add('hidden');
    return;
  }
  if (badge) {
    badge.textContent = unreadCount > 99 ? '99+' : String(unreadCount);
    badge.classList.toggle('hidden', unreadCount === 0);
  }
  if (dot) dot.classList.toggle('hidden', unreadCount === 0);
}

export async function loadNotifList(): Promise<void> {
  const list = $('#mentionList');
  if (!list) return;
  list.innerHTML = '<li class="mention-item-empty">Loading…</li>';
  try {
    const data = await api<Mention[]>('/api/mentions');
    if (!data.length) {
      list.innerHTML = '<li class="mention-item-empty">No notifications yet.</li>';
      return;
    }
    list.innerHTML = data.map(renderMentionItem).join('');
    api('/api/mentions/read', 'POST', {}).catch(() => {});
    refreshUnread();
  } catch {
    list.innerHTML = '<li class="mention-item-empty">Could not load notifications.</li>';
  }
}

// ── Wiring ───────────────────────────────────────────────
const markReadBtn = $('#mentionMarkRead');
if (markReadBtn) {
  markReadBtn.addEventListener('click', e => {
    e.stopPropagation();
    api('/api/mentions/read', 'POST', {}).catch(() => {});
    loadNotifList();
  });
}

const clearAllBtn = $('#mentionClearAll');
if (clearAllBtn) {
  clearAllBtn.addEventListener('click', e => {
    e.stopPropagation();
    if (!confirm('Clear all notifications? This cannot be undone.')) return;
    api('/api/mentions', 'DELETE')
      .then(() => {
        const list = $('#mentionList');
        if (list) list.innerHTML = '<li class="mention-item-empty">No notifications yet.</li>';
        refreshUnread();
        toast('Notifications cleared');
      })
      .catch(err => toast(err instanceof Error ? err.message : 'Could not clear notifications', true));
  });
}

const mentionList = $('#mentionList');
if (mentionList) {
  mentionList.addEventListener('click', e => {
    const item = (e.target as HTMLElement).closest<HTMLElement>('[data-stype]');
    if (!item) return;
    const stype = item.dataset.stype;
    const taskId = Number(item.dataset.task || 0);
    const channel = item.dataset.channel || '';
    $('#profileMenu')?.classList.add('hidden');
    if (stype === 'comment' && taskId && commentOpen) commentOpen(taskId);
    else if ((stype === 'chat' || stype === 'call' || stype === 'missed_call') && channel && chatOpen) chatOpen(channel);
  });
}

window.setInterval(refreshUnread, 20000);
refreshUnread();
